import { useFormik } from 'formik'
import { useSnackbar } from 'notistack'

import type { License } from '@img-arena/consoles-api-streaming'
import { Divider, FormControlLabel, Radio, RadioGroup, Stack } from '@mui/material'
import { ConsoleDialog } from '@/components/ConsoleDialog'
import { FormikCheckbox } from '@/components/inputs/FormikCheckbox'
import { GeoBlockingTemplateSelect } from '@/components/inputs/GeoBlockingTemplateSelect'
import { InputSectionDivider } from '@/components/inputs/InputSectionDivider'
import { LicensePropertyAutocomplete } from '@/components/inputs/LicensePropertyAutocomplete'
import { LicensingPeriods, StreamingLicensingPeriod } from '@/components/inputs/LicensingPeriods'
import { SelectBox } from '@/components/inputs/SelectBox'
import { streamingRestApi } from '@/service/streamingApi'
import { FormDialogProps } from '@/types/forms'
import { formikSaveButtonProps, getFormError, onFieldChange } from '@/utils/forms/formik'
import { LicenseRow } from '@/views/Operators/Operator/Licenses/licenses.dge.table'

import { DGELicenseFormValidationSchema } from './LicenseForm.dge.validation'
import { StreamingCountrySelect } from './StreamingCountrySelect'
import { generateCreateLicensePayload, generateUpdateLicensePayload } from './utils'

type Data = {
  operatorId: number
  operatorLicenses: License[]
}

export type DGELicenseFormProps = FormDialogProps<LicenseRow, Data>

const BITRATE_OPTIONS = [6000, 4500, 3000, 1640, 1100, 728, 412] as const
const DEFAULT_BITRATE = BITRATE_OPTIONS[5]

type FormikValues = {
  licenseSportPropertyId: number | undefined
  allowLowLatencyPlayer: boolean
  archiveToDve: boolean
  archiveToS3: boolean
  allowLiveDvr: boolean
  allowApiVodAccess: boolean
  allowInteractiveStreaming: boolean
  allowSrtSyndication: boolean
  bitRate: number
  autoBooking: boolean
  geoBlockingType: 'none' | 'whitelist' | 'blacklist'
  geoBlockingTerritories: string[]
  licensingPeriods: StreamingLicensingPeriod[]
}

export const DGELicenseForm = ({
  onClose,
  onSubmit,
  open = false,
  defaultValues,
  data
}: DGELicenseFormProps) => {
  const { enqueueSnackbar } = useSnackbar()
  const isEditMode = !!defaultValues?.id

  const currentLicense = data.operatorLicenses.find((license) => license.id === defaultValues?.id)
  const propertiesThatHaveExistingLicense = data.operatorLicenses
    .map((license) => license.property.id)
    .filter((id) => id !== currentLicense?.property.id)

  const formik = useFormik<FormikValues>({
    initialValues: {
      licenseSportPropertyId: currentLicense?.property.id ?? undefined,
      allowLowLatencyPlayer: currentLicense?.allowLLP ?? false,
      archiveToDve: currentLicense?.storeVod ?? false,
      archiveToS3: currentLicense?.storePermanentVod ?? false,
      allowLiveDvr: currentLicense?.allowLiveDVR ?? false,
      allowApiVodAccess: currentLicense?.allowApiVodAccess ?? false,
      allowInteractiveStreaming: currentLicense?.interactiveStreaming ?? false,
      allowSrtSyndication: currentLicense?.allowSrtSyndication ?? false,
      bitRate: currentLicense?.maxBitrateAllowed || DEFAULT_BITRATE,
      autoBooking: currentLicense?.autobookingEnabled ?? false,
      geoBlockingType:
        currentLicense && currentLicense.geoList.length
          ? currentLicense.isWhiteList
            ? 'whitelist'
            : 'blacklist'
          : 'none',
      geoBlockingTerritories: currentLicense?.geoListField
        ? JSON.parse(currentLicense.geoListField) || []
        : [],
      licensingPeriods: currentLicense?.periods
        ? currentLicense.periods.map((period) => ({
            maxEvents: period.maxEvents || 0,
            purchasedEvents: period.purchasedEvents || 0,
            startDate: period.start,
            endDate: period.end,
            year: new Date(period.start).getFullYear().toString(),
            id: period.id,
            version: period.version
          }))
        : []
    },

    validationSchema: DGELicenseFormValidationSchema,

    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (isEditMode && currentLicense?.id) {
          await streamingRestApi.endpoints.operators.updateLicense(
            currentLicense.id,
            generateUpdateLicensePayload(currentLicense, values)
          )
        } else {
          await streamingRestApi.endpoints.operators.addLicense(
            generateCreateLicensePayload(data.operatorId, values)
          )
        }

        onSubmit?.()
        enqueueSnackbar(`License ${isEditMode ? 'updated' : 'created'} successfully`, {
          variant: 'success'
        })
        setSubmitting(false)
        handleClose()
      } catch (error) {
        enqueueSnackbar('Error saving license', { variant: 'error' })
        setSubmitting(false)
        throw error // re-throw so the dialog won't close
      }
    }
  })

  const handleClose = () => {
    formik.resetForm()
    onClose?.()
  }

  return (
    <ConsoleDialog
      open={open}
      title={isEditMode ? 'Edit License' : 'Add License'}
      onClose={handleClose}
      dialogSize={false}
      width={820}
      smallTopMargin
      SaveButtonProps={formikSaveButtonProps(formik)}
    >
      <Stack direction="column" spacing={2}>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Stack direction="column" flexGrow={1}>
            <LicensePropertyAutocomplete.DGE
              onChange={(value) =>
                onFieldChange(formik, 'licenseSportPropertyId', value ?? undefined)
              }
              value={formik.values.licenseSportPropertyId}
              exclude={propertiesThatHaveExistingLicense}
              error={getFormError(formik, 'licenseSportPropertyId')}
              disabled={isEditMode}
            />
          </Stack>

          <FormikCheckbox
            formik={formik}
            name="autoBooking"
            label="Allow auto-booking"
            FormControlLabelProps={{
              sx: { pt: 1 }
            }}
          />
        </Stack>

        <Stack direction="row" spacing={2}>
          <Stack direction="column" width="40%">
            <InputSectionDivider label="Stream Settings" sx={{ mt: -0.5 }} />

            <Stack direction="column" pl={1.25} pb={0.5}>
              <FormikCheckbox
                formik={formik}
                name="allowLowLatencyPlayer"
                label="Allow Low Latency Player"
              />
              <FormikCheckbox formik={formik} name="archiveToDve" label="Archive to DVE" />
              <FormikCheckbox
                formik={formik}
                name="archiveToS3"
                label="Archive to DVE (S3 Content)"
              />
              <FormikCheckbox formik={formik} name="allowLiveDvr" label="Allow Live DVR" />
              <FormikCheckbox
                formik={formik}
                name="allowApiVodAccess"
                label="Allow API VOD Access"
              />
              <FormikCheckbox
                formik={formik}
                name="allowInteractiveStreaming"
                label="Allow Interactive Streaming"
              />

              <FormikCheckbox
                formik={formik}
                name="allowSrtSyndication"
                label="Allow SRT Syndication"
              />
            </Stack>

            <SelectBox
              options={BITRATE_OPTIONS}
              label="Bitrate Cap"
              formatInputOption={(option) => `${option} kb/s`}
              value={formik.values.bitRate}
              onChange={(value) => onFieldChange(formik, 'bitRate', value)}
            />

            <InputSectionDivider label="Geo Blocking" sx={{ pt: 1.25 }} />

            <GeoBlockingTemplateSelect
              onChange={(template) => {
                if (template) {
                  onFieldChange(
                    formik,
                    'geoBlockingType',
                    template.isWhiteList ? 'whitelist' : 'blacklist'
                  )
                  onFieldChange(formik, 'geoBlockingTerritories', template.geoListField)
                }
              }}
            />

            <RadioGroup
              value={formik.values.geoBlockingType}
              onChange={(e) => {
                const value = e.target.value as FormikValues['geoBlockingType']
                onFieldChange(formik, 'geoBlockingType', value)
                if (value === 'none') formik.setFieldValue('geoBlockingTerritories', [])
              }}
              sx={{
                pl: 1.25,
                py: 0.25,
                '& .MuiFormControlLabel-root:not(:last-child)': { mb: -0.5 }
              }}
            >
              <FormControlLabel
                control={<Radio size="small" />}
                value="none"
                label="No Restrictions"
              />
              <FormControlLabel
                control={<Radio size="small" />}
                value="whitelist"
                label="Whitelist"
              />
              <FormControlLabel
                control={<Radio size="small" />}
                value="blacklist"
                label="Blacklist"
              />
            </RadioGroup>

            <StreamingCountrySelect
              value={formik.values.geoBlockingTerritories}
              onChange={(regions) => {
                onFieldChange(formik, 'geoBlockingTerritories', regions)
              }}
              whitelist={formik.values.geoBlockingType === 'whitelist'}
              disabled={formik.values.geoBlockingType === 'none'}
            />
          </Stack>

          <Divider orientation="vertical" flexItem />

          <Stack direction="column" width="60%">
            <InputSectionDivider label="Licensing Periods" sx={{ mt: -0.5, pb: 1 }} />

            <LicensingPeriods.DGE
              height={380}
              onChange={(value) => onFieldChange(formik, 'licensingPeriods', value)}
              defaultValue={formik.values.licensingPeriods}
              autoBooking={formik.values.autoBooking}
            />
          </Stack>
        </Stack>
      </Stack>
    </ConsoleDialog>
  )
}
